import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

interface BlockLayoutProps {
  header: React.ReactNode | ((isScrolled: boolean) => React.ReactNode);
  footer?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  banner?: React.ReactNode;
}

const BlockLayout = ({ header, footer, children, className, banner }: BlockLayoutProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsScrolled(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0,
        rootMargin: "-10px 0px 0px 0px",
      },
    );

    const currentSentinel = sentinelRef.current;

    if (currentSentinel) {
      observer.observe(currentSentinel);
    }

    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel);
      }
    };
  }, []);

  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)}>
      <div className="sticky top-0 z-20 border-b border-gray-100 bg-white">
        <div className="px-6 py-4">
          {typeof header === "function" ? header(isScrolled) : header}
        </div>
      </div>

      <div className="flex-1 bg-gray-50/30">
        <div ref={sentinelRef} className="h-0 w-full" aria-hidden="true" />
        {banner && <div className="px-6 pt-6">{banner}</div>}
        <div className="space-y-6 px-6 py-6">{children}</div>
        <div className="h-4" />
      </div>

      {footer && (
        <div className="sticky bottom-0 z-10 border-t border-gray-100 bg-white">
          <div className="px-6 py-4">{footer}</div>
        </div>
      )}
    </div>
  );
};

export default BlockLayout;
